import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Phone } from "lucide-react";

const links = [
  { label: "Services", href: "#services", testid: "nav-services-link" },
  { label: "About", href: "#about", testid: "nav-about-link" },
  { label: "For Hosts", href: "#hosts", testid: "nav-hosts-link" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-white/30 shadow-[0_8px_30px_rgba(2,44,34,0.05)]"
          : "bg-background/60 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 }) || window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
          data-testid="nav-logo-button"
          aria-label="Sparkling Ambitions — back to top"
        >
          <span className="w-9 h-9 rounded-full bg-foam flex items-center justify-center text-pine transition-transform duration-500 group-hover:rotate-90">
            <Sparkles size={17} strokeWidth={1.75} />
          </span>
          <span className="font-display font-medium tracking-tight text-lg leading-none">
            Sparkling <span className="font-editorial italic font-semibold text-seateal">Ambitions</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollToSection(l.href)}
              className="text-sm font-light tracking-wide text-pine/70 hover:text-pine transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-foamdeep after:transition-[width] after:duration-300 hover:after:w-full"
              data-testid={l.testid}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <a
          href="tel:+18654550375"
          className="flex items-center gap-2 rounded-full bg-pine text-white pl-4 pr-5 py-2.5 text-sm font-normal tracking-wide hover:bg-seateal transition-colors duration-300"
          data-testid="nav-phone-button"
        >
          <Phone size={14} strokeWidth={1.75} />
          <span className="hidden sm:inline">(865) 455-0375</span>
          <span className="sm:hidden">Call</span>
        </a>
      </div>
    </motion.header>
  );
}
